import { useState } from 'react';
import { Terminal, ChevronDown, ChevronRight } from 'lucide-react';

interface LogsPanelProps {
  logs: string[];
  onClear: () => void;
}

// Collapsible terminal — collapsed by default. The studio's "Run" action appends
// one summary line per node and animates the edges; the brittle per-type play
// state machine from the old UI is gone. Panel state is local.
export function LogsPanel({ logs, onClear }: LogsPanelProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-[#191919] border border-[#2D2D2A] rounded-2xl overflow-hidden shadow-lg flex flex-col" id="workflows-terminal-terminal">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4.5 py-3 bg-[#242422] border-b border-[#2D2D2A] flex items-center justify-between cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
          <span className="text-[11px] font-mono text-slate-400 font-bold flex items-center gap-1.5">
            <Terminal className="w-3.5 h-3.5" />
            Pipeline Logs
            {logs.length > 0 && <span className="text-slate-600">({logs.length})</span>}
          </span>
        </div>
        <span className="flex items-center gap-2">
          {open && (
            <span
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="text-[10px] font-mono text-slate-500 hover:text-white border border-[#2D2D2A] px-2 py-0.5 rounded cursor-pointer"
            >
              Clear
            </span>
          )}
          {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
        </span>
      </button>

      {open && (
        <div className="p-4.5 max-h-40 overflow-y-auto font-mono text-[11px] text-emerald-400 space-y-1 leading-relaxed select-text bg-[#0E1117]">
          {logs.length === 0 ? (
            <div className="text-slate-600 text-center pt-6 italic font-mono">No logs yet. Click "Run" to preview execution.</div>
          ) : (
            logs.map((log, i) => {
              let cls = 'text-emerald-400';
              if (log.includes('[SYSTEM]')) cls = 'text-slate-400';
              if (log.includes('Node:')) cls = 'text-blue-400';
              if (log.includes('✓')) cls = 'text-emerald-300 font-semibold';
              if (log.includes('⚡')) cls = 'text-amber-300 font-semibold';
              return <div key={i} className={cls}>{log}</div>;
            })
          )}
        </div>
      )}
    </div>
  );
}
